// ** UI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import { Icon } from '@/components/common/icons';

// ** Types
import type React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

export type MobileBottomNavItem = {
	key: string;
	ariaLabel: string;
	iconName?: 'home' | 'favorites' | 'burger' | 'events' | 'profile' | 'dashboard';
	icon?: React.ReactNode;
	onClick?: () => void;
};

export type MobileBottomNavProps = {
	value: number;
	onChange: (nextValue: number) => void;
	items: MobileBottomNavItem[];
	sx?: SxProps<Theme>;
};

const IconPill = ({ active, children }: { active: boolean; children: React.ReactNode }) => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				width: 76,
				height: 56,
				borderRadius: '100px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: active ? alpha(theme.palette.grey[600], 0.12) : 'transparent',
				transition: 'background-color 180ms ease-out'
			}}
		>
			{children}
		</Box>
	);
};

const MobileBottomNav = ({ value, onChange, items, sx }: MobileBottomNavProps) => {
	const theme = useTheme();

	const handleAction = (index: number) => {
		onChange(index);

		if (items[index]?.onClick) {
			items[index].onClick?.();
		}
	};

	return (
		<Paper
			elevation={0}
			sx={[
				{
					position: 'fixed',
					left: '50%',
					bottom: `calc(${theme.spacing(4.5)} + env(safe-area-inset-bottom, 0px))`,
					transform: 'translateX(-50%)',
					width: `min(361px, calc(100vw - ${theme.spacing(12)}))`,
					borderRadius: '100px',
					bgcolor: theme.palette.text.primary,
					boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 8px 24px rgba(0,0,0,0.45)',
					overflow: 'hidden',
					zIndex: (theme) => theme.zIndex.appBar + 1
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			<BottomNavigation
				showLabels={false}
				value={value}
				onChange={(_, nextValue) => handleAction(nextValue)}
				sx={{
					bgcolor: 'transparent',
					height: 64,
					px: 1,
					'& .MuiBottomNavigationAction-root': {
						minWidth: 0,
						padding: 0,
						color: 'common.white'
					}
				}}
			>
				{items.map((item, index) => {
					const active = value === index;
					const c = active ? theme.palette.primary.main : theme.palette.common.white;

					const iconNode = item.iconName ? (
						<Icon
							name={item.iconName}
							color={c}
							{...(item.iconName === 'dashboard' && {
								sx: {
									'& svg': {
										fontSize: '2rem'
									}
								}
							})}
						/>
					) : (
						item.icon
					);

					return (
						<BottomNavigationAction
							key={item.key}
							value={index}
							icon={<IconPill active={active}>{iconNode}</IconPill>}
							aria-label={item.ariaLabel}
						/>
					);
				})}
			</BottomNavigation>
		</Paper>
	);
};

export default MobileBottomNav;
